import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const TABS = {
  pending: { id: 'pending', label: 'New requests' },
  verified: { id: 'verified', label: 'Verified pictures' },
};

function PhotoVerifications({ embedded = false }) {
  const [activeTab, setActiveTab] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPictureIndex, setSelectedPictureIndex] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [replacementImage, setReplacementImage] = useState(null);
  const [replacementImagePreview, setReplacementImagePreview] = useState(null);
  const [processingKey, setProcessingKey] = useState(null);

  const isPendingTab = activeTab === 'pending';

  const pictureKey = (userId, index) => `${userId}:${index}`;

  const userVisibleOnTab = (user, tab = activeTab) =>
    (user.profilePictures || []).some((_, index) => {
      const status = user.pictureStatuses?.[index]?.status || 'pending';
      return tab === 'pending' ? status !== 'approved' : status === 'approved';
    });

  const patchUsersList = (userId, patchFn) => {
    setUsers((prev) =>
      prev
        .map((u) => (u.userId === userId ? patchFn(u) : u))
        .filter((u) => userVisibleOnTab(u))
    );
  };

  const setPictureStatus = (user, pictureIndex, status) => {
    const pictureStatuses = [...(user.pictureStatuses || [])];
    const prev = pictureStatuses[pictureIndex] || {};
    pictureStatuses[pictureIndex] = {
      ...prev,
      status,
      validationStatus: status,
    };
    return { ...user, pictureStatuses };
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getUsersWithProfilePictures({
        page,
        limit: 20,
        tab: activeTab,
        search: searchQuery.trim() || undefined,
      });
      setUsers(response.data.users || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotal(response.data.pagination?.total ?? 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReplacementImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setReplacementImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRejectAndReplace = async () => {
    if (!selectedUser || selectedPictureIndex === null || !replacementImage) {
      alert('Please select a user, picture, and upload a replacement image');
      return;
    }
    if (!window.confirm(`Are you sure you want to replace picture #${selectedPictureIndex + 1}?`)) return;

    const actionKey = pictureKey(selectedUser.userId, selectedPictureIndex);
    try {
      setProcessingKey(actionKey);
      const formData = new FormData();
      formData.append('replacementImage', replacementImage);
      formData.append('userId', selectedUser.userId);
      formData.append('pictureIndex', selectedPictureIndex.toString());

      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.strapinapp.com'}/api/admin/portal/photo-verifications/reject-picture`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to replace picture');
      }

      const result = await response.json();
      const newPictureUrl = result.newPictureUrl;
      const userId = selectedUser.userId;
      const pictureIndex = selectedPictureIndex;

      patchUsersList(userId, (u) => {
        const profilePictures = [...u.profilePictures];
        profilePictures[pictureIndex] = newPictureUrl;
        const pictureStatuses = [...(u.pictureStatuses || [])];
        pictureStatuses[pictureIndex] = {
          status: 'pending',
          validationStatus: null,
          verificationStatus: pictureStatuses[pictureIndex]?.verificationStatus ?? null,
        };
        return { ...u, profilePictures, pictureStatuses };
      });

      setShowRejectModal(false);
      setSelectedUser(null);
      setSelectedPictureIndex(null);
      setReplacementImage(null);
      setReplacementImagePreview(null);
    } catch (err) {
      alert(err.message || 'Failed to replace picture');
    } finally {
      setProcessingKey(null);
    }
  };

  const openRejectModal = (user, pictureIndex) => {
    setSelectedUser(user);
    setSelectedPictureIndex(pictureIndex);
    setShowRejectModal(true);
    setReplacementImage(null);
    setReplacementImagePreview(null);
  };

  const copyUserId = (userId) => {
    navigator.clipboard.writeText(userId);
    alert('User ID copied to clipboard!');
  };

  const handleDeletePicture = async (user, pictureIndex, e) => {
    e.stopPropagation();
    // if (!window.confirm(`Are you sure you want to delete picture #${pictureIndex + 1}?`)) return;

    const actionKey = pictureKey(user.userId, pictureIndex);
    try {
      setProcessingKey(actionKey);
      await adminAPI.removePicture({ userId: user.userId, pictureIndex });
      patchUsersList(user.userId, (u) => ({
        ...u,
        profilePictures: u.profilePictures.filter((_, i) => i !== pictureIndex),
        pictureStatuses: (u.pictureStatuses || []).filter((_, i) => i !== pictureIndex),
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete picture');
    } finally {
      setProcessingKey(null);
    }
  };

  const handleApprovePicture = async (user, pictureIndex) => {
    const actionKey = pictureKey(user.userId, pictureIndex);
    try {
      setProcessingKey(actionKey);
      await adminAPI.approvePicture({ userId: user.userId, pictureIndex });
      patchUsersList(user.userId, (u) => setPictureStatus(u, pictureIndex, 'approved'));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to verify picture');
    } finally {
      setProcessingKey(null);
    }
  };

  const pictureMatchesTab = (status) => {
    if (isPendingTab) return status !== 'approved';
    return status === 'approved';
  };

  const renderPicturesCell = (user) => (
    <div className="flex gap-2 flex-wrap">
      {user.profilePictures.map((pictureUrl, index) => {
        const status = user.pictureStatuses?.[index]?.status || 'pending';
        if (!pictureMatchesTab(status)) return null;

        const isApproved = status === 'approved';
        const isBusy = processingKey === pictureKey(user.userId, index);

        return (
          <div key={`${user.userId}-${index}`} className="relative group">
            <img
              src={pictureUrl}
              alt={`Profile ${index + 1}`}
              className={`w-16 h-16 object-cover rounded-lg border-2 cursor-pointer transition-colors ${
                isApproved ? 'border-green-400 hover:border-green-500' : 'border-gray-200 hover:border-pink-500'
              }`}
              onClick={() => openRejectModal(user, index)}
              title={isApproved ? `Verified — click to replace #${index + 1}` : `Click to replace #${index + 1}`}
            />
            <button
              type="button"
              onClick={(e) => handleDeletePicture(user, index, e)}
              disabled={!!processingKey}
              className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold text-xs hover:bg-red-600 disabled:opacity-50 z-10"
              title="Delete picture"
            >
              ×
            </button>
            <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-semibold">
              {index + 1}
            </span>
            {isApproved ? (
              <span
                className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-[10px] font-semibold text-center rounded-b-lg py-0.5"
                title="Verified"
              >
                ✓ Verified
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprovePicture(user, index);
                }}
                disabled={!!processingKey}
                className="absolute bottom-0 left-0 right-0 bg-green-500 hover:bg-green-600 text-white text-[10px] font-semibold py-1 rounded-b-lg disabled:opacity-50 z-10"
              >
                {isBusy ? '…' : 'Verify'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  const emptyMessage =
    activeTab === 'verified'
      ? searchQuery
        ? 'No verified pictures found for this search.'
        : 'No verified profile pictures yet.'
      : searchQuery
      ? 'No pending photo requests found for this search.'
      : 'No users pending photo verification.';

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`${embedded ? 'text-2xl' : 'text-4xl'} font-bold text-gray-900`}>Profile Picture Moderation</h1>
          {embedded && <p className="text-sm text-gray-500 mt-1">Review profile pictures that did not automatically pass moderation.</p>}
        </div>
        <button
          type="button"
          onClick={fetchUsers}
          className="px-4 py-2 bg-pink-500 text-white rounded-xl font-semibold hover:bg-pink-600 self-start"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {Object.values(TABS).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`px-5 py-3 font-semibold text-sm rounded-t-xl transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-pink-600 border border-b-0 border-gray-200 -mb-px'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by user ID or email..."
          className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none text-sm"
        />
        <button
          type="submit"
          className="px-5 py-2.5 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 text-sm"
        >
          Search
        </button>
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 text-sm"
          >
            Clear
          </button>
        )}
      </form>

      {!loading && (
        <p className="text-sm text-gray-500 mb-4">
          {total} {total === 1 ? 'user' : 'users'}
          {searchQuery ? ` matching "${searchQuery}"` : ''} in {TABS[activeTab].label.toLowerCase()}
        </p>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <p className="text-gray-600 text-lg">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">User ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {isPendingTab ? 'Pending pictures' : 'Verified pictures'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-xs text-gray-600 font-mono truncate max-w-[200px]"
                          title={user.userId}
                        >
                          {user.userId}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyUserId(user.userId)}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold hover:bg-gray-200 shrink-0"
                        >
                          Copy
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.email}</td>
                    <td className="px-6 py-4">{renderPicturesCell(user)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 hover:border-pink-500"
            >
              Previous
            </button>
            <span className="text-gray-700 font-semibold">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 hover:border-pink-500"
            >
              Next
            </button>
          </div>
        </>
      )}

      {showRejectModal && selectedUser && selectedPictureIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Replace Profile Picture</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedUser(null);
                    setSelectedPictureIndex(null);
                    setReplacementImage(null);
                    setReplacementImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                {selectedUser.profileName || 'No name'} ({selectedUser.email})
              </p>
              <p className="text-gray-500 text-sm mt-1 font-mono">{selectedUser.userId}</p>
              <p className="text-gray-500 text-sm mt-1">
                Replacing picture #{selectedPictureIndex + 1}
              </p>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Picture</h3>
                <img
                  src={selectedUser.profilePictures[selectedPictureIndex]}
                  alt="Current"
                  className="w-48 h-48 object-cover rounded-xl border-2 border-gray-200"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload replacement image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none"
                />
                {replacementImagePreview && (
                  <div className="mt-4">
                    <img
                      src={replacementImagePreview}
                      alt="Preview"
                      className="w-48 h-48 object-cover rounded-xl border-2 border-gray-200"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleRejectAndReplace}
                  disabled={!!processingKey || !replacementImage}
                  className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50"
                >
                  {processingKey ? 'Processing...' : 'Replace Picture'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedUser(null);
                    setSelectedPictureIndex(null);
                    setReplacementImage(null);
                    setReplacementImagePreview(null);
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoVerifications;
